import { CheckCircle2, Lightbulb } from "lucide-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { api } from "../api/client";
import { formatDate } from "../lib/format";
import { useDebouncedValue } from "../lib/hooks";
import { Button } from "../ui/Button";
import { useToast } from "../ui/Toast";
import { toQuery } from "./api";

/** A fix that worked for someone else: no reference, names or staff-only notes. */
export interface SuggestedFix {
  title: string;
  outcome: string;
  service: string | null;
  resolved_at: string;
}

const MIN_LENGTH = 8;

/**
 * While someone describes their problem, what fixed the same thing before (DECISIONS #52).
 * Only fixes support chose to share; it never stops anyone raising the ticket.
 */
export function SuggestedFixes({ text, serviceId }: { text: string; serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const q = useDebouncedValue(text.trim(), 400);
  const fixes = useQuery({
    queryKey: ["fixes", q, serviceId],
    queryFn: ({ signal }) =>
      api<SuggestedFix[]>(`/api/fixes?${toQuery({ q, service_id: serviceId || undefined })}`, { signal }),
    enabled: q.length >= MIN_LENGTH,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const items = q.length >= MIN_LENGTH ? (fixes.data ?? []) : [];
  if (!items.length) return null;

  return (
    <section className="fix-suggestions" aria-labelledby="fix-suggestions">
      <h2 id="fix-suggestions" className="fix-title">
        <Lightbulb size={16} aria-hidden="true" />
        This might fix it
      </h2>
      <ul className="fix-list">
        {items.map((fix) => (
          <li key={`${fix.title}-${fix.resolved_at}`} className="fix-item">
            <span className="fix-problem">{fix.title}</span>
            <p className="fix-outcome">{fix.outcome}</p>
            <span className="fix-meta">
              {fix.service ? `${fix.service}, fixed ${formatDate(fix.resolved_at)}` : `Fixed ${formatDate(fix.resolved_at)}`}
            </span>
          </li>
        ))}
      </ul>
      <div className="fix-actions">
        <Button
          size="sm"
          onClick={() => {
            toast.success("Glad that helped. No ticket raised.");
            navigate("/");
          }}
        >
          <CheckCircle2 size={16} aria-hidden="true" />
          That fixed it
        </Button>
        <span className="fix-hint">Otherwise, carry on below.</span>
      </div>
    </section>
  );
}
